import { applyTaxRate, lineNet, roundedDiv } from '../money-math';
import { pctOf } from '../projects/calculation-engine';
import { evaluateQuantityFormula, QuantityFormulaError } from './quantity-formula';

/**
 * Rechenkern der Angebotskalkulation (ADR 0112) — reine Funktion, von Backend
 * und Live-Vorschau gleich genutzt. Bildet die abgelöste Kalkulations-App nach:
 *
 *   Menge     = Formel des Räumertyps (oder von Hand, „Schloss")
 *   PROGRESSIV: VK je Einheit = EK × (1 + Zuschlag)           VK/Kalk = VK × Menge
 *   RETROGRAD:  VK je Einheit = Listen-VK des Artikels         VK/Kalk = VK × Menge × (1 + Zuschlag)
 *   VK/Angebot = VK/Kalk, je Zeile von Hand überschreibbar
 *   HK = EK × (1 + MGK)     SK = HK × (1 + VGK)
 *
 * Gruppen gelten je Becken und werden × Beckenanzahl gerechnet. „Kosten
 * aufteilen" (Doku, Fracht) heißt: die Gruppe gilt einmal fürs ganze Projekt und
 * wird für die Sicht „je Becken" durch die Beckenanzahl geteilt. Optionen
 * entscheiden das je Zeile (`isPerBasin`).
 *
 * Mehrere Anlagen je Angebot: Jede Anlage hat Räumertyp, Maße, Beckenanzahl und
 * Formeln. Zeilen einer Gruppe „je Anlage" rechnen mit ihrer Anlage (× deren
 * Becken); Zeilen ohne Anlage (Gruppe „ganzes Angebot", Optionen fürs Angebot)
 * rechnen mit den Maßen der ersten Anlage und × allen Becken. Mit einer Anlage
 * ist das genau die bisherige Rechnung.
 *
 * Geld in Mikro-Einheiten, ungerundet bis zur Anzeige — wie die alte App, die
 * erst beim Darstellen auf Cent rundet (sonst weicht „EK × 4" um einen Cent ab).
 */

/** Räumertypen mit eigenen Mengenformeln (je eine Spalte in „Berechnung"). */
export const FORMULA_SCRAPER_TYPES = ['FOUR_SHAFT', 'THREE_SHAFT', 'TWO_SHAFT_BOTTOM', 'TWO_SHAFT_SURFACE'] as const;
export type FormulaScraperType = (typeof FORMULA_SCRAPER_TYPES)[number];
/** Alle Räumertypen; `OTHER` = Sonderanlage („Div" der alten App) ohne Formeln, Mengen von Hand. */
export const SCRAPER_TYPES = [...FORMULA_SCRAPER_TYPES, 'OTHER'] as const;
export type ScraperType = (typeof SCRAPER_TYPES)[number];

export const SCRAPER_TYPE_LABELS: Record<ScraperType, { de: string; en: string }> = {
  FOUR_SHAFT: { de: '4-Wellen-Räumer', en: '4-shaft scraper' },
  THREE_SHAFT: { de: '3-Wellen-Räumer', en: '3-shaft scraper' },
  TWO_SHAFT_BOTTOM: { de: '2-Wellen-Bodenräumer', en: '2-shaft bottom scraper' },
  TWO_SHAFT_SURFACE: { de: '2-Wellen-Flotaträumer', en: '2-shaft surface scraper' },
  OTHER: { de: 'Sonderanlage', en: 'Special design' },
};

export type QuoteCalcPricingMode = 'PROGRESSIVE' | 'RETROGRADE';

/** Kategorie, aus deren Menge sich die Balkenanzahl ergibt. */
export const CONVEYOR_CHAIN_CATEGORY = 'Förderkette';
/** Balkenlänge = Beckenbreite − dieses Spiel (m). */
export const FLIGHT_WIDTH_CLEARANCE_M = 0.08;

export interface ScraperDimensions {
  basinCount: number;
  lengthM: number;
  widthM: number;
  heightM: number;
  waterLevelM: number;
  /** Balkenabstand in m. */
  flightSpacingM: number;
}

/** Mengenformel je Kategorie, bereits für den gewählten Räumertyp ausgewählt. */
export type QuoteCalcFormulas = Readonly<Record<string, string | null | undefined>>;

export interface FormulaVariablesInput {
  dimensions: ScraperDimensions;
  formulas: QuoteCalcFormulas;
  flightCountOverride?: number | null;
  flightLengthOverrideM?: number | null;
}

export interface FormulaVariables {
  /** Werte für `[Feld]` in den Mengenformeln. */
  variables: Record<string, number>;
  /** Förderkettenlänge (m) laut Formel; null ohne Formel oder bei Formelfehler. */
  conveyorChainM: number | null;
  /** Ungerundete Balkenanzahl (halbe Kettenlänge ÷ Balkenabstand); null, wenn nicht berechenbar. Gerechnet wird mit dem kaufmännisch gerundeten Wert. */
  flightCountExact: number | null;
  flightCount: number;
  flightLengthM: number;
}

/** Auf 6 Nachkommastellen glätten — `6,75 − 0,08` ist in Float 6,669999…; */
const tidy = (value: number): number => Math.round(value * 1e6) / 1e6;

function tryEvaluate(formula: string | null | undefined, variables: Record<string, number>): number | null {
  if (!formula?.trim()) return null;
  try {
    return evaluateQuantityFormula(formula, variables);
  } catch (error) {
    if (error instanceof QuantityFormulaError) return null;
    throw error;
  }
}

/**
 * Leitet die Formel-Felder aus den Beckenmaßen ab. Die Balkenanzahl hängt an der
 * Förderkette: halbe Kettenlänge ÷ Balkenabstand, kaufmännisch gerundet. Balkenanzahl und
 * Balkenlänge sind von Hand überschreibbar.
 */
export function deriveFormulaVariables(input: FormulaVariablesInput): FormulaVariables {
  const { dimensions, formulas } = input;
  const variables: Record<string, number> = {
    Laenge: dimensions.lengthM,
    Breite: dimensions.widthM,
    Hoehe: dimensions.heightM,
    Wasserspiegel: dimensions.waterLevelM,
  };

  const conveyorChainM = tryEvaluate(formulas[CONVEYOR_CHAIN_CATEGORY], variables);
  const flightCountExact =
    conveyorChainM !== null && dimensions.flightSpacingM > 0
      ? conveyorChainM / 2 / dimensions.flightSpacingM
      : null;
  // Kaufmännisch gerundet wie die alte App (15,2 → 15, 21,8 → 22; geprüft an allen 105
  // Kalkulationen des Exports). Die Toleranz fängt Float-Reste wie 10,4999999999 ab.
  const flightCount =
    input.flightCountOverride ?? (flightCountExact === null ? 0 : Math.max(0, Math.floor(flightCountExact + 0.5 + 1e-9)));
  const flightLengthM = input.flightLengthOverrideM ?? tidy(Math.max(0, dimensions.widthM - FLIGHT_WIDTH_CLEARANCE_M));

  variables.AnzahlBalken = flightCount;
  variables.Balkenlaenge = flightLengthM;
  variables.GleitstreifenLinks = tryEvaluate(formulas['Gleitstreifen links'], variables) ?? 0;
  variables.GleitstreifenRechts = tryEvaluate(formulas['Gleitstreifen rechts'], variables) ?? 0;
  variables.AnzahlGleitstreifen = variables.GleitstreifenLinks + variables.GleitstreifenRechts;

  return { variables, conveyorChainM, flightCountExact, flightCount, flightLengthM };
}

export interface QuoteCalcLineInput {
  id: string;
  category: string;
  /** EK je Einheit (Mikro). */
  purchasePriceMicros: bigint;
  /** Listen-VK des Artikels je Einheit (Mikro) — zählt nur RETROGRAD. */
  listPriceMicros: bigint;
  /** Menge von Hand (Schloss); ersetzt die Formel. */
  quantityOverride?: number | null;
  /** VK je Einheit von Hand; ersetzt Zuschlag bzw. Listenpreis. */
  unitPriceOverrideMicros?: bigint | null;
  /** VK/Angebot der Zeile von Hand (Zeilensumme). */
  offerTotalOverrideMicros?: bigint | null;
  /** Nur Optionen: true = je Becken (× Beckenanzahl), false = pauschal. Standard true. */
  isPerBasin?: boolean;
  /** Anlage der Zeile; null/fehlend = ganzes Angebot (bzw. erste Anlage in Gruppen „je Anlage"). */
  plantId?: string | null;
}

/** Gilt die Gruppe je Anlage oder einmal fürs ganze Angebot? */
export type QuoteCalcGroupScope = 'PLANT' | 'PROJECT';

export interface QuoteCalcPlantInput {
  id: string;
  dimensions: ScraperDimensions;
  formulas: QuoteCalcFormulas;
  flightCountOverride?: number | null;
  flightLengthOverrideM?: number | null;
  /** Preis der Anlage fest (Mikro); null = aus dem Gesamtpreis verteilt. */
  priceLockMicros?: bigint | null;
}

export interface QuoteCalcGroupInput {
  key: string;
  pricingMode: QuoteCalcPricingMode;
  /** Zuschlag in % (negativ = Abschlag, z. B. −30 auf den Listenpreis). */
  surchargePct: number;
  /** „Kosten aufteilen": Gruppe gilt einmal fürs Projekt statt je Becken. */
  isSplit: boolean;
  /** Optionen zählen nicht zum Angebotspreis. */
  isOptions: boolean;
  /** Standard PLANT; Optionen entscheiden je Zeile über `plantId`. */
  scope?: QuoteCalcGroupScope;
  lines: QuoteCalcLineInput[];
}

export interface QuoteCalcInput {
  dimensions: ScraperDimensions;
  formulas: QuoteCalcFormulas;
  flightCountOverride?: number | null;
  flightLengthOverrideM?: number | null;
  groups: QuoteCalcGroupInput[];
  /** Materialgemeinkosten in %. */
  materialOverheadPct: number;
  /** Vertriebsgemeinkosten in % auf die HK. */
  salesOverheadPct: number;
  /** Verkäuferprovision in % vom Angebotspreis. */
  commissionPct: number;
  /** Angebotspreis ohne Optionen, alle Becken (Mikro); null = noch keiner. */
  finalPriceMicros: bigint | null;
  /** Anlagen; fehlt die Liste, ist die Kalkulation eine Anlage aus `dimensions`/`formulas`. */
  plants?: QuoteCalcPlantInput[];
}

/** Die vier Summenspalten der alten App. */
export interface QuoteCalcAmounts {
  /** EK/Ges. */
  purchaseMicros: bigint;
  /** VK/Ges. (VK je Einheit × Menge). */
  salesMicros: bigint;
  /** VK/Kalk. */
  calculatedMicros: bigint;
  /** VK/Angebot. */
  offerMicros: bigint;
}

export interface QuoteCalcLineResult {
  id: string;
  formula: string | null;
  /** Menge laut Formel (ohne Formel 1, bei Formelfehler 0). */
  autoQuantity: number;
  formulaError: string | null;
  quantity: number;
  isQuantityOverridden: boolean;
  unitSalesPriceMicros: bigint;
  amounts: QuoteCalcAmounts;
  /** Anlage, mit der die Zeile gerechnet wurde; null = ganzes Angebot. */
  plantId: string | null;
  /** Wie oft die Zeile zählt: Becken ihrer Anlage (bzw. aller Anlagen) oder 1. */
  factor: number;
  /** Zeile × factor — das zählt in die Summen. */
  totalAmounts: QuoteCalcAmounts;
}

export interface QuoteCalcGroupResult {
  key: string;
  lines: QuoteCalcLineResult[];
  /** Summe der Zeilen, wie sie in der Tabelle stehen. */
  lineTotals: QuoteCalcAmounts;
  /** Sicht „je Becken" (aufgeteilte Gruppen ÷ Beckenanzahl), mit HK und SK der Gruppe. */
  perBasin: QuoteCalcCostBlock;
  /** Sicht „alle Becken" (je-Becken-Gruppen × Beckenanzahl), mit HK und SK der Gruppe. */
  allBasins: QuoteCalcCostBlock;
}

export interface QuoteCalcCostBlock extends QuoteCalcAmounts {
  /** HK = EK × (1 + MGK). */
  manufacturingCostMicros: bigint;
  /** SK = HK × (1 + VGK). */
  totalCostMicros: bigint;
}

export interface QuoteCalcPlantResult {
  id: string;
  basinCount: number;
  variables: FormulaVariables;
  /** Eigene Zeilen der Anlage ohne Optionen (alle Becken). */
  own: QuoteCalcCostBlock;
  /** Je Gruppe die eigenen Zeilen der Anlage. */
  groups: { key: string; allBasins: QuoteCalcCostBlock }[];
}

export interface QuoteCalcPlantPricing {
  id: string;
  priceMicros: bigint;
  isLocked: boolean;
  /** SK der Anlage plus ihr Anteil an den gemeinsamen Leistungen. */
  totalCostMicros: bigint;
  resultMicros: bigint;
  resultPct: number | null;
}

export interface QuoteCalcResult {
  /** Formel-Felder der ersten Anlage (bei einer Anlage: der Kalkulation). */
  variables: FormulaVariables;
  /** Becken aller Anlagen zusammen. */
  basinCount: number;
  plants: QuoteCalcPlantResult[];
  /** Zeilen „ganzes Angebot" ohne Optionen. */
  project: { own: QuoteCalcCostBlock; groups: { key: string; allBasins: QuoteCalcCostBlock }[] };
  groups: QuoteCalcGroupResult[];
  withoutOptions: { perBasin: QuoteCalcCostBlock; allBasins: QuoteCalcCostBlock };
  withOptions: { perBasin: QuoteCalcCostBlock; allBasins: QuoteCalcCostBlock };
  /** Bewertung des Angebotspreises gegen „ohne Optionen, alle Becken"; null ohne Preis. */
  pricing: {
    finalPriceMicros: bigint;
    /** Angebotspreis − kalkuliertes VK/Angebot. */
    deviationMicros: bigint;
    commissionMicros: bigint;
    /** Angebotspreis − SK − Provision. */
    resultMicros: bigint;
    resultPct: number | null;
    /** EK ÷ Angebotspreis. */
    wesPct: number | null;
    /** Gesamtpreis auf die Anlagen verteilt (gemeinsame Leistungen anteilig, feste Preise bleiben). */
    plants: QuoteCalcPlantPricing[];
  } | null;
}

const ZERO: QuoteCalcAmounts = { purchaseMicros: 0n, salesMicros: 0n, calculatedMicros: 0n, offerMicros: 0n };

const addAmounts = (a: QuoteCalcAmounts, b: QuoteCalcAmounts): QuoteCalcAmounts => ({
  purchaseMicros: a.purchaseMicros + b.purchaseMicros,
  salesMicros: a.salesMicros + b.salesMicros,
  calculatedMicros: a.calculatedMicros + b.calculatedMicros,
  offerMicros: a.offerMicros + b.offerMicros,
});

const mapAmounts = (a: QuoteCalcAmounts, fn: (value: bigint) => bigint): QuoteCalcAmounts => ({
  purchaseMicros: fn(a.purchaseMicros),
  salesMicros: fn(a.salesMicros),
  calculatedMicros: fn(a.calculatedMicros),
  offerMicros: fn(a.offerMicros),
});

/** Betrag × (1 + Prozent), exakt in Mikro. */
const withPct = (amount: bigint, pct: number): bigint => applyTaxRate(amount, 100 + pct);

interface LineContext {
  plantId: string | null;
  formulas: QuoteCalcFormulas;
  variables: Record<string, number>;
  /** Becken, mit denen die Zeile zählt (eigene Anlage bzw. alle). */
  basins: number;
  totalBasins: bigint;
}

function computeLine(line: QuoteCalcLineInput, group: QuoteCalcGroupInput, context: LineContext): QuoteCalcLineResult {
  const { formulas, variables } = context;
  const formula = formulas[line.category]?.trim() || null;
  let autoQuantity = 1;
  let formulaError: string | null = null;
  if (formula) {
    try {
      autoQuantity = evaluateQuantityFormula(formula, variables);
    } catch (error) {
      if (!(error instanceof QuantityFormulaError)) throw error;
      autoQuantity = 0;
      formulaError = error.message;
    }
  }
  const isQuantityOverridden = line.quantityOverride !== null && line.quantityOverride !== undefined;
  const quantity = isQuantityOverridden ? line.quantityOverride! : autoQuantity;

  const isProgressive = group.pricingMode === 'PROGRESSIVE';
  const unitSalesPriceMicros =
    line.unitPriceOverrideMicros ??
    (isProgressive ? withPct(line.purchasePriceMicros, group.surchargePct) : line.listPriceMicros);
  const purchaseMicros = lineNet(line.purchasePriceMicros, quantity);
  const salesMicros = lineNet(unitSalesPriceMicros, quantity);
  const calculatedMicros = isProgressive ? salesMicros : withPct(salesMicros, group.surchargePct);
  const offerMicros = line.offerTotalOverrideMicros ?? calculatedMicros;
  const amounts = { purchaseMicros, salesMicros, calculatedMicros, offerMicros };
  const isOnce = group.isOptions ? !(line.isPerBasin ?? true) : group.isSplit;
  const factor = isOnce ? 1 : context.basins;

  return {
    id: line.id,
    formula,
    autoQuantity,
    formulaError,
    quantity,
    isQuantityOverridden,
    unitSalesPriceMicros,
    amounts,
    plantId: context.plantId,
    factor,
    totalAmounts: mapAmounts(amounts, (value) => value * BigInt(factor)),
  };
}

function computeGroup(
  group: QuoteCalcGroupInput,
  contextFor: (line: QuoteCalcLineInput, group: QuoteCalcGroupInput) => LineContext,
  totalBasins: bigint,
  costBlock: (amounts: QuoteCalcAmounts) => QuoteCalcCostBlock,
): QuoteCalcGroupResult {
  const lines = group.lines.map((line) => computeLine(line, group, contextFor(line, group)));
  const lineTotals = lines.reduce((sum, line) => addAmounts(sum, line.amounts), ZERO);
  // „Je Becken" = Zeile × eigener Faktor ÷ alle Becken. Mit einer Anlage: je-Becken-Zeilen
  // unverändert, einmal gezählte geteilt — wie bisher.
  const perBasin = lines.reduce(
    (sum, line) => addAmounts(sum, line.factor === Number(totalBasins) ? line.amounts : mapAmounts(line.amounts, (value) => roundedDiv(value * BigInt(line.factor), totalBasins))),
    ZERO,
  );
  const allBasins = lines.reduce((sum, line) => addAmounts(sum, line.totalAmounts), ZERO);
  return { key: group.key, lines, lineTotals, perBasin: costBlock(perBasin), allBasins: costBlock(allBasins) };
}

/** Gesamtpreis auf die Anlagen verteilen: Basis = eigenes Angebot + Anteil gemeinsamer Leistungen; feste Preise bleiben, der Rest im Verhältnis, auf 10 € gerundet, die letzte freie Anlage nimmt die Rundung. */
const PRICE_STEP_MICROS = 10_000_000n;
function allocatePlantPrices(
  finalPriceMicros: bigint,
  plants: { id: string; offerMicros: bigint; costMicros: bigint; lock: bigint | null }[],
  project: { offerMicros: bigint; costMicros: bigint },
  commissionPct: number,
): QuoteCalcPlantPricing[] {
  const plantOffer = plants.reduce((sum, plant) => sum + plant.offerMicros, 0n);
  const shareOf = (value: bigint, plant: (typeof plants)[number]) =>
    plantOffer > 0n ? roundedDiv(value * plant.offerMicros, plantOffer) : roundedDiv(value, BigInt(plants.length));
  const base = plants.map((plant) => plant.offerMicros + shareOf(project.offerMicros, plant));
  const lockedSum = plants.reduce((sum, plant) => sum + (plant.lock ?? 0n), 0n);
  const freeIndexes = plants.map((plant, index) => (plant.lock === null ? index : -1)).filter((index) => index >= 0);
  const freeBase = freeIndexes.reduce((sum, index) => sum + base[index]!, 0n);
  let rest = finalPriceMicros - lockedSum;
  const prices = plants.map((plant) => plant.lock ?? 0n);
  freeIndexes.forEach((index, position) => {
    if (position === freeIndexes.length - 1) {
      prices[index] = rest;
      return;
    }
    const raw = freeBase > 0n ? roundedDiv((finalPriceMicros - lockedSum) * base[index]!, freeBase) : 0n;
    prices[index] = roundedDiv(raw, PRICE_STEP_MICROS) * PRICE_STEP_MICROS;
    rest -= prices[index]!;
  });
  return plants.map((plant, index) => {
    const priceMicros = prices[index]!;
    const totalCostMicros = plant.costMicros + shareOf(project.costMicros, plant);
    const resultMicros = priceMicros - totalCostMicros - applyTaxRate(priceMicros, commissionPct);
    return { id: plant.id, priceMicros, isLocked: plant.lock !== null, totalCostMicros, resultMicros, resultPct: pctOf(resultMicros, priceMicros) };
  });
}

/**
 * Rechnet eine komplette Kalkulation. Die Beckenanzahl wird auf eine ganze Zahl
 * ≥ 1 gebracht; alle anderen Eingaben gehen unverändert ein.
 */
export function computeQuoteCalculation(input: QuoteCalcInput): QuoteCalcResult {
  const plantInputs: QuoteCalcPlantInput[] = input.plants?.length
    ? input.plants
    : [{ id: 'main', dimensions: input.dimensions, formulas: input.formulas, flightCountOverride: input.flightCountOverride, flightLengthOverrideM: input.flightLengthOverrideM }];
  const plants = plantInputs.map((plant) => ({
    input: plant,
    basins: Math.max(1, Math.round(plant.dimensions.basinCount || 1)),
    variables: deriveFormulaVariables(plant),
  }));
  const first = plants[0]!;
  const basinCount = plants.reduce((sum, plant) => sum + plant.basins, 0);
  const totalBasins = BigInt(basinCount);
  // HK = EK × (1 + MGK), SK = HK × (1 + VGK) — je Gruppe, je Anlage und für die Summen.
  const costBlock = (amounts: QuoteCalcAmounts): QuoteCalcCostBlock => {
    const manufacturingCostMicros = withPct(amounts.purchaseMicros, input.materialOverheadPct);
    return {
      ...amounts,
      manufacturingCostMicros,
      totalCostMicros: withPct(manufacturingCostMicros, input.salesOverheadPct),
    };
  };
  const contextFor = (line: QuoteCalcLineInput, group: QuoteCalcGroupInput): LineContext => {
    const own = plants.find((plant) => plant.input.id === line.plantId);
    // Gruppen „je Anlage": ohne gültige Anlage zählt die Zeile zur ersten (alte Kalkulationen).
    const plant = own ?? (!group.isOptions && (group.scope ?? 'PLANT') === 'PLANT' ? first : null);
    return plant
      ? { plantId: plant.input.id, formulas: plant.input.formulas, variables: plant.variables.variables, basins: plant.basins, totalBasins }
      : { plantId: null, formulas: first.input.formulas, variables: first.variables.variables, basins: basinCount, totalBasins };
  };
  const groups = input.groups.map((group) => computeGroup(group, contextFor, totalBasins, costBlock));

  // Summen aus den reinen Beträgen (nicht aus den Gruppen-HK), damit nur einmal gerundet wird.
  const amountsOf = ({ purchaseMicros, salesMicros, calculatedMicros, offerMicros }: QuoteCalcAmounts): QuoteCalcAmounts => ({ purchaseMicros, salesMicros, calculatedMicros, offerMicros });
  const sum = (filter: (group: QuoteCalcGroupResult, index: number) => boolean, view: 'perBasin' | 'allBasins') =>
    groups.filter(filter).reduce((total, group) => addAmounts(total, amountsOf(group[view])), ZERO);
  const isMain = (_: QuoteCalcGroupResult, index: number) => !input.groups[index]!.isOptions;
  const any = () => true;

  const withoutOptions = {
    perBasin: costBlock(sum(isMain, 'perBasin')),
    allBasins: costBlock(sum(isMain, 'allBasins')),
  };
  const withOptions = {
    perBasin: costBlock(sum(any, 'perBasin')),
    allBasins: costBlock(sum(any, 'allBasins')),
  };

  // Je Anlage und „ganzes Angebot": eigene Zeilen ohne Optionen.
  const mainGroups = groups.filter((_, index) => !input.groups[index]!.isOptions);
  const ownOf = (plantId: string | null) => {
    const byGroup = mainGroups
      .map((group) => ({ key: group.key, amounts: group.lines.filter((line) => line.plantId === plantId).reduce((total, line) => addAmounts(total, line.totalAmounts), ZERO), count: group.lines.filter((line) => line.plantId === plantId).length }))
      .filter((group) => group.count > 0);
    return { own: costBlock(byGroup.reduce((total, group) => addAmounts(total, group.amounts), ZERO)), groups: byGroup.map((group) => ({ key: group.key, allBasins: costBlock(group.amounts) })) };
  };
  const plantResults: QuoteCalcPlantResult[] = plants.map((plant) => ({ id: plant.input.id, basinCount: plant.basins, variables: plant.variables, ...ownOf(plant.input.id) }));
  const project = ownOf(null);

  let pricing: QuoteCalcResult['pricing'] = null;
  if (input.finalPriceMicros !== null) {
    const finalPriceMicros = input.finalPriceMicros;
    const base = withoutOptions.allBasins;
    const commissionMicros = applyTaxRate(finalPriceMicros, input.commissionPct);
    const resultMicros = finalPriceMicros - base.totalCostMicros - commissionMicros;
    pricing = {
      finalPriceMicros,
      deviationMicros: finalPriceMicros - base.offerMicros,
      commissionMicros,
      resultMicros,
      resultPct: pctOf(resultMicros, finalPriceMicros),
      wesPct: pctOf(base.purchaseMicros, finalPriceMicros),
      plants: allocatePlantPrices(
        finalPriceMicros,
        plantResults.map((plant, index) => ({ id: plant.id, offerMicros: plant.own.offerMicros, costMicros: plant.own.totalCostMicros, lock: plantInputs[index]!.priceLockMicros ?? null })),
        { offerMicros: project.own.offerMicros, costMicros: project.own.totalCostMicros },
        input.commissionPct,
      ),
    };
  }

  return { variables: first.variables, basinCount, plants: plantResults, project, groups, withoutOptions, withOptions, pricing };
}

/** Preis kaufmännisch auf ein Raster runden („Kalkulierten Preis übernehmen", z. B. 100 €). */
/**
 * Angebotspreis, bei dem das Ergebnis auf SK `targetPct` % des Preises ist:
 * Preis − SK − Provision(Preis) = Ziel × Preis  ⇒  Preis = SK ÷ (1 − Provision − Ziel).
 * Auf `stepMicros` gerundet; null, wenn Provision + Ziel ≥ 100 %.
 */
export function priceForTargetResult(totalCostMicros: bigint, commissionPct: number, targetPct: number, stepMicros: bigint): bigint | null {
  const rest = 10_000n - BigInt(Math.round(commissionPct * 100)) - BigInt(Math.round(targetPct * 100));
  if (rest <= 0n) return null;
  return roundPriceToStep(roundedDiv(totalCostMicros * 10_000n, rest), stepMicros);
}

export function roundPriceToStep(priceMicros: bigint, stepMicros: bigint): bigint {
  if (stepMicros <= 0n) return priceMicros;
  return roundedDiv(priceMicros, stepMicros) * stepMicros;
}
